import { KeySet } from '@tableau/data-structures';
import { CatalogUrlParamKeys } from '@tableau/shared-urls/lib/src/SiteUrls';
import React from 'react';
import { defaultMemoize } from 'reselect';
import {
  getFirstParamValueAsSet,
  getSingleParamValue,
  paramValuesMapToSearch,
  removeParamValue,
  searchToParamValuesMap,
  setParamValues,
  UrlSearch,
  UrlSearchProvider,
} from '../../../data-catalog-shared';

export function constructNewSearchForFlowOutputFieldSelection(search: string, flowOutputFieldId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const flowOutputFieldIds = paramValuesMap[CatalogUrlParamKeys.field];
  if (flowOutputFieldIds && flowOutputFieldIds.indexOf(flowOutputFieldId) >= 0) {
    removeParamValue(paramValuesMap, CatalogUrlParamKeys.field, flowOutputFieldId);
  } else {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, [flowOutputFieldId]);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchWithClearedFlowOutputFieldSelection(search: string): string {
  const paramValuesMap = searchToParamValuesMap(search);
  const flowOutputFieldIds = paramValuesMap[CatalogUrlParamKeys.field];
  if (flowOutputFieldIds) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, []);
  }
  return paramValuesMapToSearch(paramValuesMap);
}

export function constructNewSearchForOutputStepSelection(search: string, outputStepId: string): string {
  const paramValuesMap = searchToParamValuesMap(search);

  // If we're switching to a new output step, clear the field filter.
  const outputSteps = paramValuesMap[CatalogUrlParamKeys.outputStep];
  const currentOutputStepId = outputSteps && outputSteps.length > 0 ? outputSteps[0] : null;
  if (currentOutputStepId !== outputStepId) {
    setParamValues(paramValuesMap, CatalogUrlParamKeys.field, []);
  }

  setParamValues(paramValuesMap, CatalogUrlParamKeys.outputStep, [outputStepId]);
  return paramValuesMapToSearch(paramValuesMap);
}

export type FlowSubplaceParams = {
  readonly urlSearchValue: string;
  readonly selectedFlowOutputFieldIds: KeySet;
  readonly selectedOutputStepId: string;
  readonly updateFlowOutputFieldSelection: (flowOutputFieldId: string) => void;
  readonly clearFlowOutputFieldSelection: () => void;
  readonly updateOutputStepSelection: (outputStepId: string) => void;
};

export type FlowSubplaceParamsProviderProps = {
  readonly children: (params: FlowSubplaceParams) => React.ReactNode;
};

export class FlowSubplaceParamsProvider extends React.Component<FlowSubplaceParamsProviderProps> {

  public override render() {
    return (
      <UrlSearchProvider>
        {urlSearch =>
          <FlowSubplaceParamsProviderInternal urlSearch={urlSearch}>
            {this.props.children}
          </FlowSubplaceParamsProviderInternal>
        }
      </UrlSearchProvider>
    );
  }

}

type FlowSubplaceParamsProviderInternalProps = {
  readonly urlSearch: UrlSearch;
  readonly children: (params: FlowSubplaceParams) => React.ReactNode;
};

class FlowSubplaceParamsProviderInternal extends React.Component<FlowSubplaceParamsProviderInternalProps> {

  private readonly memoizedGetFirstParamValueAsSet = defaultMemoize(getFirstParamValueAsSet);
  private readonly memoizedGetSingleParamValue = defaultMemoize(getSingleParamValue);

  public override render() {
    return this.props.children({
      urlSearchValue: this.props.urlSearch.value,
      selectedFlowOutputFieldIds: this.memoizedGetFirstParamValueAsSet(this.props.urlSearch.value, CatalogUrlParamKeys.field),
      selectedOutputStepId: this.memoizedGetSingleParamValue(this.props.urlSearch.value, CatalogUrlParamKeys.outputStep),
      updateFlowOutputFieldSelection: this.updateFlowOutputFieldSelection,
      clearFlowOutputFieldSelection: this.clearFlowOutputFieldSelection,
      updateOutputStepSelection: this.updateOutputStepSelection
    });
  }

  private readonly clearFlowOutputFieldSelection = () => {
    const urlSearch = this.props.urlSearch;
    const newSearchWithClearedFieldSelection =
      constructNewSearchWithClearedFlowOutputFieldSelection(urlSearch.value);
    urlSearch.update(newSearchWithClearedFieldSelection);
  }

  private readonly updateFlowOutputFieldSelection = (fieldId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForFlowOutputFieldSelection(urlSearch.value, fieldId);
    urlSearch.update(newSearch);
  }

  private readonly updateOutputStepSelection = (outputStepId: string) => {
    const urlSearch = this.props.urlSearch;
    const newSearch = constructNewSearchForOutputStepSelection(urlSearch.value, outputStepId);
    urlSearch.update(newSearch);
  }
}
